import { apiRequest } from './axiosConfig';

/**
 * admin「行動分析」タブの API クライアント（/api/admin/behavior/*）。
 * レスポンスは { success, data } で、ここでは data だけ返す。
 */

export interface BehaviorRange {
  from: string;
  to: string;
  device?: string;
}

export interface BehaviorTotals {
  sessions: number;
  users: number;
  anonymous: number;
  page_views: number;
  clicks: number;
  api_errors: number;
  js_errors: number;
}

export interface BehaviorDailyPoint {
  date: string;
  sessions: number;
  users: number;
  page_views: number;
  clicks: number;
  api_errors: number;
  js_errors: number;
}

export interface BehaviorDeviceShare {
  device_category: string;
  sessions: number;
}

export interface PageStat {
  page_pattern: string;
  views: number;
  unique_users: number;
  unique_sessions: number;
  avg_duration_ms: number | null;
  median_duration_ms: number | null;
  p90_duration_ms: number | null;
  exit_rate: number;
  api_error_rate: number;
  top_clicks: { event_name: string; count: number }[];
}

export interface BehaviorOverview {
  totals: BehaviorTotals;
  daily: BehaviorDailyPoint[];
  devices: BehaviorDeviceShare[];
  top_pages: PageStat[];
}

export interface FunnelDefinition {
  key: string;
  label: string;
  actor: 'user' | 'anonymous';
  window_days: number;
  steps: { label: string }[];
}

export interface FunnelStep {
  label: string;
  count: number;
  conversion_rate: number;
  drop_off_rate: number;
  median_ms_to_next: number | null;
  p90_ms_to_next: number | null;
}

export interface FunnelResult extends Omit<FunnelDefinition, 'steps'> {
  steps: FunnelStep[];
}

export interface SessionSummary {
  session_id: string;
  user_id: number | null;
  user_name: string | null;
  user_email: string | null;
  organization_id: number | null;
  organization_name: string | null;
  started_at: string;
  ended_at: string;
  duration_ms: number;
  page_views: number;
  clicks: number;
  errors: number;
  first_page: string | null;
  last_page: string | null;
  device_category: string | null;
  browser: string | null;
  os: string | null;
}

export interface SessionEvent {
  id: number;
  event_type: string;
  event_name: string;
  occurred_at: string;
  page_path: string | null;
  duration_ms: number | null;
  props: Record<string, unknown> | null;
  elapsed_ms: number;
}

export interface SessionDetail {
  session: SessionSummary;
  events: SessionEvent[];
}

export interface SessionSearchParams extends BehaviorRange {
  user_id?: number;
  organization_id?: number;
  anonymous_id?: string;
  email?: string;
  limit?: number;
}

export interface GenerationTiming {
  type: string;
  count: number;
  failed: number;
  p50_ms: number | null;
  p90_ms: number | null;
  max_ms: number | null;
}

export interface ApiTiming {
  event_name: string;
  count: number;
  p50_ms: number | null;
  p90_ms: number | null;
}

export interface SlowOperations {
  generations: GenerationTiming[];
  api: ApiTiming[];
}

export type BehaviorDailyReportStatus = 'pending' | 'generating' | 'ready' | 'failed';

/** 日次レポート一覧の 1 行（本文は含まない） */
export interface BehaviorDailyReportSummary {
  id: number;
  report_date: string;
  status: BehaviorDailyReportStatus;
  model: string | null;
  generated_at: string | null;
  error: string | null;
  has_narrative: boolean;
}

/**
 * サーバーが決定的に集計した metrics。画面で使うキーだけ型を付け、
 * 残り（entry_pages / transitions / funnels など）は index signature で受ける。
 */
export interface BehaviorDailyReportMetrics {
  totals: BehaviorTotals;
  totals_7d_avg: BehaviorTotals;
  session_stats: {
    sessions: number;
    with_user: number;
    single_pv: number;
    under_10s: number;
    avg_pv: number;
    avg_dur_ms: number;
    with_errors: number;
  };
  generation: {
    started_sessions: number;
    completed_sessions: number;
    failed_sessions: number;
    dl_sessions: number;
    started_no_result: number;
  };
  [key: string]: unknown;
}

export interface BehaviorDailyReport extends Omit<BehaviorDailyReportSummary, 'has_narrative'> {
  period_from: string;
  period_to: string;
  input_tokens: number;
  output_tokens: number;
  metrics: BehaviorDailyReportMetrics | null;
  narrative: string | null;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

const unwrap = async <T>(request: Promise<Envelope<T>>): Promise<T> => {
  const response = await request;
  if (!response.success) {
    throw new Error('行動分析データの取得に失敗しました');
  }
  return response.data;
};

const adminBehaviorService = {
  getOverview: (range: BehaviorRange): Promise<BehaviorOverview> =>
    unwrap(apiRequest.get<Envelope<BehaviorOverview>>('admin/behavior/overview', { params: range })),

  getPages: (range: BehaviorRange): Promise<PageStat[]> =>
    unwrap(apiRequest.get<Envelope<PageStat[]>>('admin/behavior/pages', { params: range })),

  getFunnels: (): Promise<FunnelDefinition[]> =>
    unwrap(apiRequest.get<Envelope<FunnelDefinition[]>>('admin/behavior/funnels')),

  getFunnel: (key: string, range: BehaviorRange): Promise<FunnelResult> =>
    unwrap(apiRequest.get<Envelope<FunnelResult>>(`admin/behavior/funnels/${encodeURIComponent(key)}`, { params: range })),

  getSessions: (params: SessionSearchParams): Promise<SessionSummary[]> =>
    unwrap(apiRequest.get<Envelope<SessionSummary[]>>('admin/behavior/sessions', { params })),

  getSession: (sessionId: string): Promise<SessionDetail> =>
    unwrap(apiRequest.get<Envelope<SessionDetail>>(`admin/behavior/sessions/${encodeURIComponent(sessionId)}`)),

  getSlowOperations: (range: BehaviorRange): Promise<SlowOperations> =>
    unwrap(apiRequest.get<Envelope<SlowOperations>>('admin/behavior/slow-operations', { params: range })),

  getDailyReports: (): Promise<BehaviorDailyReportSummary[]> =>
    unwrap(apiRequest.get<Envelope<BehaviorDailyReportSummary[]>>('admin/behavior/daily-reports')),

  getDailyReport: (date: string): Promise<BehaviorDailyReport> =>
    unwrap(apiRequest.get<Envelope<BehaviorDailyReport>>(`admin/behavior/daily-reports/${encodeURIComponent(date)}`)),

  /** 再生成は 202 を返し、行は generating になる（生成そのものはキュー Job） */
  regenerateDailyReport: (date: string): Promise<BehaviorDailyReport> =>
    unwrap(
      apiRequest.post<Envelope<BehaviorDailyReport>>(
        `admin/behavior/daily-reports/${encodeURIComponent(date)}/regenerate`
      )
    ),
};

export default adminBehaviorService;
